/*
 * The filter toolbar from Figma `08 — App Shell & Templates` — search, filter chips and the screen's
 * own controls in one row above a table.
 *
 * It owns no filter state. Every one of these lists is filtered by the endpoint that serves it, so
 * the toolbar reports what the person typed and chose and the screen decides what that means.
 *
 * The search box is a `search` input inside a `role="search"` region with a real label, because the
 * magnifier glyph is not a name and a bare input above a table announces nothing.
 */
import { useId } from 'react';
import type { ReactNode } from 'react';
import { IconSearch } from './icons';

export interface FilterChip<K extends string = string> {
  key: K;
  label: string;
  count?: number;
}

export function FilterToolbar({ children }: { children: ReactNode }) {
  return <div className="jt-toolbar">{children}</div>;
}

export function SearchField({ value, onChange, label, placeholder }: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <div className="jt-search" role="search">
      <label className="jt-sr-only" htmlFor={id}>{label}</label>
      <span className="jt-search-icon" aria-hidden="true"><IconSearch size={16} /></span>
      <input
        id={id}
        type="search"
        className="jt-input jt-search-input"
        value={value}
        placeholder={placeholder ?? label}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * The status chips above a queue.
 *
 * A radio group rather than a row of buttons: exactly one is in effect at a time, which is what a
 * radio group means, and arrow keys then move between them for free.
 */
export function FilterChips<K extends string>({ chips, active, onSelect, label }: {
  chips: readonly FilterChip<K>[];
  active: K;
  onSelect: (key: K) => void;
  label: string;
}) {
  return (
    <div className="jt-chips" role="radiogroup" aria-label={label}>
      {chips.map((chip) => {
        const on = chip.key === active;
        return (
          <button
            key={chip.key}
            type="button"
            role="radio"
            aria-checked={on}
            className="jt-chip"
            data-active={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onSelect(chip.key)}
          >
            {chip.label}
            {chip.count !== undefined && chip.count > 0 && <span className="jt-tab-count">{chip.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The filters currently in effect, each removable on its own, plus one way to clear the lot.
 *
 * The frozen design draws this as `Filter Bar / Applied` (`109:51`, `119:326`) and it is the half
 * that makes the filtered-empty state honest: a table saying "nothing matched" is only actionable
 * if the thing that matched nothing is still on screen and can be taken off. A filter you cannot
 * see is a filter you will not think to remove.
 *
 * Each pill's remove button names the filter it drops — "ลบตัวกรอง สถานะ: รอตรวจ" — because five
 * buttons all called "ลบ" are five identical announcements.
 */
export interface AppliedFilter {
  key: string;
  /** What was filtered on, e.g. "สถานะ". */
  field: string;
  /** What it was set to, e.g. "รอตรวจ". */
  value: string;
}

export function AppliedFilters({ filters, onRemove, onClearAll }: {
  filters: readonly AppliedFilter[];
  onRemove: (key: string) => void;
  onClearAll?: () => void;
}) {
  if (filters.length === 0) return null;
  return (
    <div className="jt-applied" role="group" aria-label="ตัวกรองที่ใช้อยู่">
      {filters.map((filter) => (
        <span key={filter.key} className="jt-applied-pill">
          <span className="jt-applied-field">{filter.field}:</span>
          <span className="jt-applied-value">{filter.value}</span>
          <button
            type="button"
            className="jt-applied-remove"
            aria-label={`ลบตัวกรอง ${filter.field}: ${filter.value}`}
            onClick={() => onRemove(filter.key)}
          >
            ✕
          </button>
        </span>
      ))}
      {onClearAll && filters.length > 1 && (
        <button type="button" className="jt-applied-clear" onClick={onClearAll}>
          ล้างตัวกรองทั้งหมด
        </button>
      )}
    </div>
  );
}

/**
 * How much the current filters left, said in words above the list.
 *
 * `total` is the unfiltered size where the screen knows it. Saying "42 จาก 1,204" is the difference
 * between a narrow filter and a broken query, and someone deciding whether to widen the filter
 * needs both numbers, not the one in front of them.
 */
export function ResultSummary({ shown, total, label, busy = false }: {
  shown: number;
  total?: number;
  /** What is being counted, e.g. "ออร์เดอร์". */
  label: string;
  busy?: boolean;
}) {
  const th = (n: number) => n.toLocaleString('th-TH');
  return (
    <p className="jt-result-summary" aria-live="polite" aria-busy={busy || undefined}>
      {busy
        ? `กำลังนับ${label}…`
        : total === undefined
          ? `พบ ${th(shown)} ${label}`
          : shown === total
            ? `แสดง${label}ทั้งหมด ${th(total)} รายการ`
            : `พบ ${th(shown)} จาก ${th(total)} ${label}`}
    </p>
  );
}
